import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Bell, MapPin, Users, Search, X, Mail, Building2, UserPlus, Download, Link as LinkIcon, Check, XCircle } from "lucide-react";
import GoogleCalendarPanel from "../components/GoogleCalendarPanel";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Date());
  const [upcoming, setUpcoming] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", start_date: "", start_time: "09:00",
    end_date: "", end_time: "10:00", location: "", reminder_minutes: 15, recurrence: "none",
  });
  // Invite state
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteMode, setInviteMode] = useState("search"); // search | email | job
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEmails, setInviteEmails] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [notify, setNotify] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedDialog, setFeedDialog] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");

  const openFeedDialog = async () => {
    try {
      const r = await api.get("/calendar/feed-url");
      setFeedUrl(r.data.url);
      setFeedDialog(true);
    } catch { toast.error("Errore"); }
  };

  const copyFeedUrl = () => {
    navigator.clipboard.writeText(feedUrl);
    toast.success("Link copiato!");
  };

  const downloadIcs = async (eventId) => {
    try {
      const token = localStorage.getItem("tb_token");
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiUrl}/api/events/${eventId}/ics`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `evento.ics`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Errore download"); }
  };

  const load = async () => {
    const r = await api.get("/events"); setEvents(r.data);
    const u = await api.get("/events/upcoming"); setUpcoming(u.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await api.get("/events/upcoming");
        r.data.forEach((e) => {
          if (e.minutes_until <= (e.reminder_minutes || 15) && e.minutes_until >= 0) {
            const key = `notif_${e.id}_${Math.floor(Date.now() / 300000)}`;
            if (!localStorage.getItem(key)) {
              localStorage.setItem(key, "1");
              toast.message(`🔔 ${e.title}`, { description: `Tra ${e.minutes_until} min${e.location ? " · " + e.location : ""}`, duration: 8000 });
              if ("Notification" in window && Notification.permission === "granted") new Notification(`Verixa · ${e.title}`, { body: `Tra ${e.minutes_until} minuti` });
            }
          }
        });
      } catch {}
    }, 60000);
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    return () => clearInterval(id);
  }, []);

  // Autocomplete ricerca
  useEffect(() => {
    if (inviteMode !== "search") return;
    if (!searchQ || searchQ.length < 1) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try { setSearchResults((await api.get(`/employees/search?q=${encodeURIComponent(searchQ)}`)).data); }
      catch { setSearchResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [searchQ, inviteMode]);

  // Carica reparti quando si apre il dialog
  useEffect(() => {
    if (!inviteFor) return;
    api.get("/employees/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, [inviteFor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { const d = new Date(e.start).toDateString(); map[d] = map[d] || []; map[d].push(e); });
    return map;
  }, [events]);

  const dayEvents = events.filter((e) => new Date(e.start).toDateString() === selected?.toDateString());

  const openNew = () => {
    const d = (selected || new Date()).toISOString().slice(0, 10);
    setForm({ ...form, start_date: d, end_date: d });
    setOpen(true);
  };

  const save = async () => {
    try {
      const start = new Date(`${form.start_date}T${form.start_time}:00`).toISOString();
      const end = new Date(`${form.end_date || form.start_date}T${form.end_time}:00`).toISOString();
      await api.post("/events", {
        title: form.title, description: form.description, start, end,
        location: form.location, reminder_minutes: Number(form.reminder_minutes),
        recurrence: form.recurrence, attendees: [],
      });
      toast.success("Evento creato"); setOpen(false); load();
    } catch (e) { toast.error("Errore creazione evento"); }
  };

  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Eliminato"); load(); };

  // ---- Invite ----
  const openInvite = (ev) => {
    setInviteFor(ev);
    setInviteMode("search");
    setSearchQ(""); setSearchResults([]); setSelectedIds([]);
    setInviteEmail(""); setInviteEmails([]);
    setSelectedJob(""); setNotify(true);
  };

  const toggleSelectId = (id) => {
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const addEmail = () => {
    const em = inviteEmail.trim().toLowerCase();
    if (!em || !em.includes("@")) { toast.error("Email non valida"); return; }
    if (inviteEmails.includes(em)) return;
    setInviteEmails([...inviteEmails, em]); setInviteEmail("");
  };

  const submitInvite = async () => {
    if (!inviteFor) return;
    setSending(true);
    try {
      const payload = { notify };
      if (inviteMode === "search") payload.employee_ids = selectedIds;
      else if (inviteMode === "email") payload.emails = inviteEmails;
      else if (inviteMode === "job") payload.job = selectedJob;

      const empty = (!payload.employee_ids?.length && !payload.emails?.length && !payload.job);
      if (empty) { toast.error("Seleziona almeno un invitato"); setSending(false); return; }

      const r = await api.post(`/events/${inviteFor.id}/invite`, payload);
      toast.success(`${r.data.invited_count} invitati${r.data.emails_sent ? ` · ${r.data.emails_sent} email inviate` : ""}`);
      setInviteFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invito"); }
    finally { setSending(false); }
  };

  return (
    <div className="p-4 md:p-12" data-testid="calendar-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Calendario Intelligente</div>
          <h1 className="font-display text-4xl">Agenda</h1>
          <p className="text-[#555] mt-1 text-sm">Promemoria automatici · Inviti per dipendente, email o reparto · Ricorrenze</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openFeedDialog} data-testid="subscribe-calendar-btn" className="rounded-full h-11 border-[#1F2540]">
            <LinkIcon className="w-4 h-4 mr-2"/> Sottoscrivi
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="new-event-btn" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] h-11 btn-glow"><Plus className="w-4 h-4 mr-2"/> Nuovo evento</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl border-0 max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo appuntamento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titolo</Label><Input data-testid="event-title" className="rounded-xl mt-1" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})}/></div>
              <div><Label>Descrizione</Label><Textarea className="rounded-xl mt-1" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}/></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Data inizio</Label><Input type="date" className="rounded-xl mt-1" value={form.start_date} onChange={(e)=>setForm({...form, start_date: e.target.value})}/></div>
                <div><Label>Ora inizio</Label><Input type="time" className="rounded-xl mt-1" value={form.start_time} onChange={(e)=>setForm({...form, start_time: e.target.value})}/></div>
                <div><Label>Data fine</Label><Input type="date" className="rounded-xl mt-1" value={form.end_date} onChange={(e)=>setForm({...form, end_date: e.target.value})}/></div>
                <div><Label>Ora fine</Label><Input type="time" className="rounded-xl mt-1" value={form.end_time} onChange={(e)=>setForm({...form, end_time: e.target.value})}/></div>
              </div>
              <div><Label>Luogo</Label><Input className="rounded-xl mt-1" value={form.location} onChange={(e)=>setForm({...form, location: e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Promemoria</Label>
                  <Select value={String(form.reminder_minutes)} onValueChange={(v)=>setForm({...form, reminder_minutes: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 min prima</SelectItem>
                      <SelectItem value="15">15 min prima</SelectItem>
                      <SelectItem value="30">30 min prima</SelectItem>
                      <SelectItem value="60">1 ora prima</SelectItem>
                      <SelectItem value="1440">1 giorno prima</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ricorrenza</Label>
                  <Select value={form.recurrence} onValueChange={(v)=>setForm({...form, recurrence: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna</SelectItem>
                      <SelectItem value="daily">Giornaliera</SelectItem>
                      <SelectItem value="weekly">Settimanale</SelectItem>
                      <SelectItem value="monthly">Mensile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="event-save" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Salva evento</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-6">
        <GoogleCalendarPanel />
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5 card-smart p-6">
          <Calendar mode="single" selected={selected} onSelect={setSelected}
            showOutsideDays={false}
            locale={undefined}
            modifiers={{ hasEvent: Object.keys(eventsByDate).map((d) => new Date(d)) }}
            modifiersClassNames={{ hasEvent: "bg-[#3B82F6] text-white hover:bg-[#7C3AED] font-bold" }}
            className="w-full"/>
          <div className="mt-4 text-xs text-[#757575] flex items-center gap-2 justify-center">
            <span className="w-3 h-3 rounded-full bg-[#3B82F6] inline-block"/> Giorno con eventi
            <span className="text-[#999] ml-4">Clicca una data per vedere i dettagli</span>
          </div>
          <div className="mt-6 border-t border-[#1F2540] pt-4">
            <div className="text-xs uppercase tracking-widest text-[#757575] mb-3">Prossimi 24h</div>
            <ul className="space-y-2 text-sm">
              {upcoming.slice(0, 4).map((e) => (
                <li key={e.id} className="flex items-center justify-between">
                  <span className="truncate">{e.title}</span>
                  <span className="text-xs bg-[#0A0A0A] text-white px-2 py-0.5 rounded-full ml-2">{e.minutes_until}m</span>
                </li>
              ))}
              {upcoming.length === 0 && <li className="text-[#757575]">Nessuno.</li>}
            </ul>
          </div>
        </div>

        <div className="md:col-span-7 card-smart p-8" data-testid="day-events">
          <h3 className="font-display text-2xl mb-4">{selected?.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</h3>
          {dayEvents.length === 0 ? (
            <div className="text-sm text-[#757575]">Nessun evento programmato per questo giorno.</div>
          ) : (
            <ul className="divide-y divide-[#1F2540]">
              {dayEvents.map((e) => (
                <li key={e.id} className="py-4 flex items-start justify-between gap-4">
                  <div className="flex gap-4 flex-1">
                    <div className="w-1 bg-[#3B82F6] self-stretch rounded-full"/>
                    <div className="flex-1">
                      <div className="font-semibold">{e.title}</div>
                      <div className="text-xs text-[#757575]">
                        {new Date(e.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} –{" "}
                        {new Date(e.end).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {e.location && <div className="text-xs mt-1 flex items-center gap-1"><MapPin className="w-3 h-3"/>{e.location}</div>}
                      {e.description && <div className="text-sm mt-2 text-[#555]">{e.description}</div>}
                      <div className="text-xs mt-2 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1 text-[#3B82F6]"><Bell className="w-3 h-3"/>{e.reminder_minutes}m</span>
                        {(e.attendees?.length || 0) > 0 && <span className="flex items-center gap-1 text-[#757575]"><Users className="w-3 h-3"/>{e.attendees.length} invitati</span>}
                        {(e.extra_invitees?.length || 0) > 0 && <span className="flex items-center gap-1 text-[#757575]"><Mail className="w-3 h-3"/>{e.extra_invitees.length} esterni</span>}
                        {(() => {
                          const yes = (e.rsvp_responses || []).filter(r => r.status === "yes").length;
                          const no = (e.rsvp_responses || []).filter(r => r.status === "no").length;
                          if (yes === 0 && no === 0) return null;
                          return (
                            <>
                              {yes > 0 && <span className="flex items-center gap-1 text-green-700 font-semibold"><Check className="w-3 h-3"/>{yes}</span>}
                              {no > 0 && <span className="flex items-center gap-1 text-red-600 font-semibold"><XCircle className="w-3 h-3"/>{no}</span>}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={()=>downloadIcs(e.id)} data-testid={`ics-event-${e.id}`} title="Scarica .ics" className="rounded-full hover:bg-[#1E2347]"><Download className="w-4 h-4 text-[#3B82F6]"/></Button>
                    <Button variant="ghost" size="icon" onClick={()=>openInvite(e)} data-testid={`invite-event-${e.id}`} title="Invita dipendenti" className="rounded-full hover:bg-[#1E2347]"><UserPlus className="w-4 h-4 text-[#3B82F6]"/></Button>
                    <Button variant="ghost" size="icon" onClick={()=>del(e.id)} data-testid={`delete-event-${e.id}`} className="rounded-full hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Dialog invito */}
      <Dialog open={!!inviteFor} onOpenChange={(v)=>{ if(!v) setInviteFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="invite-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Invita dipendenti</DialogTitle></DialogHeader>
          {inviteFor && (
            <div className="space-y-4">
              <div className="bg-[#1E2347]/50 border border-[#1F2540] rounded-xl p-3 text-sm">
                <div className="font-semibold">{inviteFor.title}</div>
                <div className="text-xs text-[#757575]">{new Date(inviteFor.start).toLocaleString("it-IT")}</div>
              </div>

              {/* Tabs modalità */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: "search", label: "Cerca", icon: Search },
                  { id: "email", label: "Email", icon: Mail },
                  { id: "job", label: "Reparto", icon: Building2 },
                ].map((m) => {
                  const Icon = m.icon;
                  const active = inviteMode === m.id;
                  return (
                    <button key={m.id} onClick={()=>setInviteMode(m.id)} data-testid={`invite-mode-${m.id}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-2 transition-all ${active ? "bg-[#3B82F6] text-white" : "bg-white border border-[#1F2540] hover:border-[#3B82F6]"}`}>
                      <Icon className="w-3.5 h-3.5"/> {m.label}
                    </button>
                  );
                })}
              </div>

              {/* MODE: Cerca */}
              {inviteMode === "search" && (
                <div className="space-y-2">
                  <Label>Cerca per nome, cognome o email</Label>
                  <Input data-testid="invite-search-input" autoFocus className="rounded-xl" value={searchQ} onChange={(e)=>setSearchQ(e.target.value)} placeholder="Es. Luca Bianchi"/>
                  {searchResults.length > 0 && (
                    <div className="border border-[#1F2540] rounded-xl max-h-56 overflow-auto divide-y divide-[#1F2540]">
                      {searchResults.map((e) => (
                        <button key={e.id} type="button" onClick={()=>toggleSelectId(e.id)} data-testid={`search-result-${e.id}`}
                          className={`w-full text-left px-3 py-2 hover:bg-[#1E2347]/50 flex items-center justify-between ${selectedIds.includes(e.id) ? "bg-[#1E2347]" : ""}`}>
                          <div>
                            <div className="font-semibold text-sm">{e.name} {e.lastname}</div>
                            <div className="text-xs text-[#757575]">{e.email}{e.job && ` · ${e.job}`}</div>
                          </div>
                          {selectedIds.includes(e.id) && <span className="text-xs bg-[#3B82F6] text-white px-2 py-0.5 rounded-full">Scelto</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedIds.length > 0 && <div className="text-xs text-[#757575]">{selectedIds.length} dipendenti selezionati</div>}
                </div>
              )}

              {/* MODE: Email */}
              {inviteMode === "email" && (
                <div className="space-y-2">
                  <Label>Inserisci email</Label>
                  <div className="flex gap-2">
                    <Input data-testid="invite-email-input" type="email" className="rounded-xl" value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} placeholder="nome@azienda.it" onKeyDown={(e)=>e.key === "Enter" && (e.preventDefault(), addEmail())}/>
                    <Button onClick={addEmail} type="button" data-testid="invite-email-add" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]"><Plus className="w-4 h-4"/></Button>
                  </div>
                  {inviteEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {inviteEmails.map((em) => (
                        <span key={em} className="inline-flex items-center gap-1 bg-[#1E2347] text-[#7C3AED] text-xs px-3 py-1 rounded-full font-semibold">
                          {em}
                          <button onClick={()=>setInviteEmails(inviteEmails.filter((x)=>x!==em))} className="hover:text-red-600"><X className="w-3 h-3"/></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* MODE: Reparto */}
              {inviteMode === "job" && (
                <div className="space-y-2">
                  <Label>Seleziona mansione/reparto</Label>
                  {jobs.length === 0 ? (
                    <div className="text-xs text-[#757575] italic">Nessun reparto/mansione trovato. Compila il campo "Mansione" o "Reparto" nell'anagrafica dei dipendenti.</div>
                  ) : (
                    <Select value={selectedJob || undefined} onValueChange={setSelectedJob}>
                      <SelectTrigger data-testid="invite-job-select" className="rounded-xl"><SelectValue placeholder="Scegli un reparto…"/></SelectTrigger>
                      <SelectContent>
                        {jobs.map((j) => <SelectItem key={j.label} value={j.label}>{j.label} ({j.count} persone)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Notify toggle */}
              <label className="flex items-center gap-2 text-sm text-[#555] cursor-pointer">
                <input type="checkbox" checked={notify} onChange={(e)=>setNotify(e.target.checked)} data-testid="invite-notify" className="w-4 h-4 accent-[#3B82F6]"/>
                Invia email di invito (richiede Resend attivo)
              </label>

              <DialogFooter>
                <Button onClick={submitInvite} disabled={sending} data-testid="invite-submit" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">
                  {sending ? "Invio…" : "Invia inviti"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog sottoscrizione feed iCal */}
      <Dialog open={feedDialog} onOpenChange={setFeedDialog}>
        <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="feed-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Sottoscrivi il calendario</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#555]">Aggiungi il calendario aziendale al tuo <strong>Google Calendar</strong>, <strong>Apple Calendar</strong> o <strong>Outlook</strong>: ogni evento creato apparirà automaticamente.</p>

            <div className="bg-[#1E2347]/50 border border-[#1F2540] rounded-xl p-3">
              <Label>Link di sottoscrizione iCal</Label>
              <div className="mt-2 flex gap-2">
                <Input data-testid="feed-url-input" value={feedUrl} readOnly className="rounded-xl font-mono text-xs bg-white"/>
                <Button onClick={copyFeedUrl} data-testid="feed-url-copy" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Copia</Button>
              </div>
            </div>

            <div className="text-sm space-y-2">
              <div className="font-semibold">Come aggiungerlo:</div>
              <div className="text-xs text-[#555] space-y-1 pl-4">
                <div>📆 <strong>Google Calendar</strong>: impostazioni → "Aggiungi calendario" → "Da URL" → incolla il link</div>
                <div>🍎 <strong>Apple Calendar</strong>: File → Nuova sottoscrizione calendario → incolla il link</div>
                <div>📧 <strong>Outlook</strong>: Aggiungi calendario → Sottoscrivi da Web → incolla il link</div>
              </div>
            </div>

            <div className="text-[10px] text-[#888] italic">⚠️ Il link contiene un token personale valido 1 anno: non condividerlo.</div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
